import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Card,
  CardBody,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  useColorModeValue,
  useToast,
  Grid,
  GridItem,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useTeamService } from '@/hooks/useTeamService';

// Icons
const MoreIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

interface TeamInvitation {
  id: string;
  team_id: string;
  inviter_id: string;
  invitee_email?: string;
  invitee_username?: string;
  role: string;
  message?: string;
  status: string;
  created_at: string;
  expires_at?: string;
  accepted_at?: string;
  declined_at?: string;
}

interface TeamInvitationsTabProps {
  teamId: string;
  permissions: {
    can_view_invitations: boolean;
    can_invite_users: boolean;
    can_cancel_invitation: boolean;
  };
  isActive: boolean;
  onAcceptInvitation?: (invitationId: string) => void;
  onDeclineInvitation?: (invitationId: string) => void;
}

export const TeamInvitationsTab: React.FC<TeamInvitationsTabProps> = ({
  teamId,
  permissions,
  isActive,
  onAcceptInvitation,
  onDeclineInvitation,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { colorSchemes } = useThemeContext();
  const teamService = useTeamService();

  // Color mode values
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const cardBg = useColorModeValue('white', 'gray.800');

  // State
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load invitations data only when tab becomes active
  const loadInvitations = async () => {
    if (!teamId || !permissions.can_view_invitations) return;

    try {
      setIsLoading(true);
      setError('');
      
      const invitationsData = await teamService.getTeamInvitations(teamId);
      setInvitations(invitationsData || []);
      setHasLoaded(true);
    } catch (err: any) {
      console.error('Error loading team invitations:', err);
      setError(err.message || 'Failed to load team invitations');
      toast({
        title: t('common.error'),
        description: err.message || 'Failed to load team invitations',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when tab becomes active
  useEffect(() => {
    if (isActive && !hasLoaded && permissions.can_view_invitations) {
      loadInvitations();
    }
  }, [isActive, hasLoaded, permissions.can_view_invitations, teamId]);

  // Get status badge color
  const getStatusBadgeColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'yellow';
      case 'accepted':
        return 'green';
      case 'declined':
        return 'red';
      case 'expired':
        return 'gray';
      case 'cancelled':
        return 'orange';
      default:
        return 'gray';
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'owner':
        return 'purple';
      case 'admin':
        return 'blue';
      case 'moderator':
        return 'green';
      case 'member':
        return 'gray';
      default:
        return 'gray';
    }
  };

  // Handle accept invitation
  const handleAcceptInvitation = async (invitation: TeamInvitation) => {
    try {
      if (onAcceptInvitation) {
        await onAcceptInvitation(invitation.id);
        await loadInvitations(); // Reload after action
      }
    } catch (error) {
      console.error('Error accepting invitation:', error);
    }
  };

  // Handle decline invitation
  const handleDeclineInvitation = async (invitation: TeamInvitation) => {
    try {
      if (onDeclineInvitation) {
        await onDeclineInvitation(invitation.id);
        await loadInvitations(); // Reload after action
      }
    } catch (error) {
      console.error('Error declining invitation:', error);
    }
  };

  // Handle cancel invitation
  const handleCancelInvitation = async (invitation: TeamInvitation) => {
    if (!permissions.can_cancel_invitation) return;
    
    if (window.confirm('Are you sure you want to cancel this invitation?')) {
      try {
        await teamService.cancelTeamInvitation(teamId, invitation.id);
        await loadInvitations(); // Reload after action
        
        toast({
          title: t('teams.invitationCancelled'),
          description: 'Invitation has been cancelled successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error: any) {
        console.error('Error cancelling invitation:', error);
        toast({
          title: t('common.error'),
          description: error.message || 'Failed to cancel invitation',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // Check if invitation is expired
  const isExpired = (invitation: TeamInvitation) => {
    if (!invitation.expires_at) return false;
    return new Date(invitation.expires_at) < new Date();
  };

  // Get invitation recipient display
  const getRecipientDisplay = (invitation: TeamInvitation) => {
    return invitation.invitee_username || invitation.invitee_email || 'Unknown';
  };

  // Permission check
  if (!permissions.can_view_invitations) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="warning">
          <AlertIcon />
          {t('teams.noPermissionToViewInvitations')}
        </Alert>
      </Box>
    );
  }

  // Loading state
  if (isLoading && !hasLoaded) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="lg" />
        <Text>{t('teams.loadingInvitations')}</Text>
      </VStack>
    );
  }

  // Error state
  if (error && !isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
        <Button mt={4} onClick={loadInvitations} colorScheme={colorSchemes.primary}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  // Empty state
  if (!isLoading && invitations.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color={textColor} fontSize="lg">
          {t('teams.noInvitations')}
        </Text>
        {permissions.can_invite_users && (
          <Text color={textColor} fontSize="sm" mt={2}>
            {t('teams.sendFirstInvitation')}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Header */}
      <HStack justify="space-between" align="center">
        <Text fontSize="lg" fontWeight="semibold">
          {t('teams.teamInvitations')} ({invitations.length})
        </Text>
        <Button
          size="sm"
          onClick={loadInvitations}
          isLoading={isLoading}
          colorScheme={colorSchemes.neutral}
          variant="outline"
        >
          {t('common.refresh')}
        </Button>
      </HStack>

      {/* Invitations Grid */}
      <Grid templateColumns="repeat(auto-fill, minmax(350px, 1fr))" gap={4}>
        {invitations.map((invitation) => (
          <GridItem key={invitation.id}>
            <Card
              bg={cardBg}
              borderWidth={1}
              borderColor={borderColor}
              transition="all 0.2s"
              _hover={{
                transform: 'translateY(-2px)',
                shadow: 'md',
                borderColor: `${colorSchemes.primary}.300`,
              }}
            >
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  {/* Header with recipient and status */}
                  <HStack justify="space-between" align="start">
                    <HStack spacing={3}>
                      <Avatar
                        size="sm"
                        name={getRecipientDisplay(invitation)}
                        bg={`${colorSchemes.primary}.500`}
                        color="white"
                      />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="semibold" fontSize="md">
                          {getRecipientDisplay(invitation)}
                        </Text>
                        <Text fontSize="sm" color={textColor}>
                          {invitation.invitee_email && invitation.invitee_username 
                            ? invitation.invitee_email 
                            : (invitation.invitee_email ? 'Email invitation' : 'Username invitation')}
                        </Text>
                      </VStack>
                    </HStack>

                    {/* Actions Menu */}
                    <Menu>
                      <MenuButton
                        as={IconButton}
                        icon={<MoreIcon />}
                        size="sm"
                        variant="ghost"
                      />
                      <MenuList>
                        {invitation.status === 'pending' && (
                          <>
                            <MenuItem
                              icon={<CheckIcon />}
                              onClick={() => handleAcceptInvitation(invitation)}
                            >
                              {t('teams.acceptInvitation')}
                            </MenuItem>
                            <MenuItem
                              icon={<CloseIcon />}
                              onClick={() => handleDeclineInvitation(invitation)}
                            >
                              {t('teams.declineInvitation')}
                            </MenuItem>
                            {permissions.can_cancel_invitation && (
                              <MenuItem
                                color="red.500"
                                onClick={() => handleCancelInvitation(invitation)}
                              >
                                {t('teams.cancelInvitation')}
                              </MenuItem>
                            )}
                          </>
                        )}
                        {invitation.status !== 'pending' && permissions.can_cancel_invitation && (
                          <MenuItem
                            color="red.500"
                            onClick={() => handleCancelInvitation(invitation)}
                          >
                            {t('teams.removeInvitation')}
                          </MenuItem>
                        )}
                      </MenuList>
                    </Menu>
                  </HStack>

                  {/* Role and Status */}
                  <HStack spacing={2}>
                    <Badge
                      colorScheme={getRoleBadgeColor(invitation.role)}
                      variant="subtle"
                      size="sm"
                    >
                      {invitation.role || 'Member'}
                    </Badge>
                    <Badge
                      colorScheme={getStatusBadgeColor(invitation.status)}
                      variant="solid"
                      size="sm"
                    >
                      {invitation.status}
                    </Badge>
                    {isExpired(invitation) && (
                      <Badge colorScheme="red" variant="outline" size="sm">
                        {t('common.expired')}
                      </Badge>
                    )}
                  </HStack>

                  {/* Message */}
                  {invitation.message && (
                    <Box
                      p={3}
                      bg={useColorModeValue('gray.50', 'gray.700')}
                      borderRadius="md"
                    >
                      <Text fontSize="sm" color={textColor}>
                        "{invitation.message}"
                      </Text>
                    </Box>
                  )}

                  {/* Dates */}
                  <VStack align="stretch" spacing={1}>
                    <Text fontSize="xs" color={textColor}>
                      {t('teams.invitedOn')}: {new Date(invitation.created_at).toLocaleDateString()}
                    </Text>
                    {invitation.expires_at && (
                      <Text fontSize="xs" color={textColor}>
                        {t('teams.expiresOn')}: {new Date(invitation.expires_at).toLocaleDateString()}
                      </Text>
                    )}
                    {invitation.accepted_at && (
                      <Text fontSize="xs" color="green.500">
                        {t('teams.acceptedOn')}: {new Date(invitation.accepted_at).toLocaleDateString()}
                      </Text>
                    )}
                    {invitation.declined_at && (
                      <Text fontSize="xs" color="red.500">
                        {t('teams.declinedOn')}: {new Date(invitation.declined_at).toLocaleDateString()}
                      </Text>
                    )}
                  </VStack>

                  {/* Actions for pending invitations */}
                  {invitation.status === 'pending' && !isExpired(invitation) && (
                    <HStack spacing={2} pt={2}>
                      <Button
                        size="sm"
                        colorScheme="green"
                        leftIcon={<CheckIcon />}
                        onClick={() => handleAcceptInvitation(invitation)}
                        flex={1}
                      >
                        {t('teams.accept')}
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        leftIcon={<CloseIcon />}
                        onClick={() => handleDeclineInvitation(invitation)}
                        flex={1}
                      >
                        {t('teams.decline')}
                      </Button>
                    </HStack>
                  )}
                </VStack>
              </CardBody>
            </Card>
          </GridItem>
        ))}
      </Grid>
    </VStack>
  );
}; 